// Help & Guides — verbatim port of source-of-truth.
//
// Source: /var/www/kitchenos/portal-standalone.html
//   Page-section markup → line 2961 (#page-help)
//   Scoped CSS         → lines 22492–22542 (see ./styles.css — ported verbatim)
//   IIFE (content + behaviour) → lines 22543–22906
//     - content arrays (GETTING_STARTED, CRM_GUIDE, … TIPS, VIDEOS, FAQ)
//     - accordionHtml / buildContent helpers
//     - ensurePage / attachBehaviours / hookNav glue (replaced by router lifecycle)
//
// Departures from source:
//  - `onclick="this.parentNode.classList.toggle('open')"` inline handlers
//    swapped for a single delegated click listener scoped to the page root.
//  - `onclick="alert(…)"` on video tiles → ctx.showToast (no native alert).
//  - `window.__KOS_HELP_PAGE__` / `hookNav(navPortalGlobal)` / `ensurePage`
//    removed — the router owns mount/unmount.
//  - `window.kosShowOnboarding` is not in the modular shell yet — the
//    "Replay onboarding" link toasts a "coming soon" message instead.
//  - Inline-styled SVGs ported verbatim — they're the source-of-truth
//    iconography for this page.

import "./styles.css";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

// ── HTML escape (verbatim source line 22549) ────────────────────────────────

function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

// ── Content arrays — verbatim from source lines 22562–22709 ─────────────────

interface QA { q: string; a: string }

const GETTING_STARTED: QA[] = [
  { q: "Step 1 -- Set up your studio profile",
    a: '<p>Head to <b>Settings &rarr; Branding</b> to upload your logo and choose your brand colour. These appear on all customer-facing documents: quotes, invoices, delivery notes and your customer portal.</p><p>Also set your <b>trading name, address, VAT number</b> and <b>default lead times</b> here. Getting this right first means every document looks professional from day one.</p><div class="tip">&#128161; Tip: Use a square PNG logo at 400&times;400 px or larger for the sharpest results on PDFs.</div>' },
  { q: "Step 2 -- Connect your trade accounts",
    a: '<p>Open <b>Settings &rarr; Integrations</b> and link your accounts with TKC, PWS, Omega, Blum, Hettich, H&auml;fele and others. Once connected, live trade prices, current stock levels and lead times appear directly inside the Quote Builder &mdash; no more switching between tabs or calling reps.</p><ul><li>Click the supplier logo to begin the OAuth or API-key flow</li><li>Test the connection with the <b>Sync now</b> button</li><li>Prices update automatically every 4 hours</li></ul>' },
  { q: "Step 3 -- Add your existing contacts",
    a: '<p>From <b>Contacts</b>, you can <b>export your current customer list as CSV</b> for backup. To add contacts, use the <b>+ Add trade contact</b> button on the Contacts page, or convert a Submissions lead into the CRM. Bulk CSV <em>import</em> is on the way — for now, add customers one at a time or contact support if you have a large list to bring across.</p>' },
  { q: "Step 4 -- Build your first quote",
    a: '<p>Open <b>Quote Builder</b> from the top nav or press <code>&#8984;K</code> and search &ldquo;new quote&rdquo;. The workflow is:</p><ul><li>Pick or create a customer</li><li>Add rooms (kitchen, utility, etc.)</li><li>Drag units from the catalogue &mdash; trade prices auto-populate</li><li>Set your margin in the margin calculator (bottom bar)</li><li>Click <b>Send</b> to email a branded PDF and a live viewer link</li></ul><div class="tip">&#128161; The customer viewer link lets clients approve the quote and pay a deposit online &mdash; no back-and-forth needed.</div>' },
  { q: "Step 5 -- Schedule a consultation",
    a: '<p>Open <b>Calendar</b>, click any free slot and choose <b>Consultation</b>. Enter the customer name, location and any notes. The customer receives an automatic confirmation email with an <em>add-to-calendar</em> link and a reminder 24 hours before. Reschedules sync back automatically.</p>' },
  { q: "Step 6 -- Invite your team",
    a: '<p>Go to <b>Settings &rarr; Team &amp; Permissions</b> and click <b>Invite user</b>. Assign one of four roles:</p><ul><li><b>Owner</b> &mdash; full access including billing</li><li><b>Manager</b> &mdash; all features except billing</li><li><b>Designer / Sales</b> &mdash; quotes, CRM, calendar, designs; no financials</li><li><b>Fitter</b> &mdash; delivery and job cards only</li></ul><p>Team members receive a secure sign-up link valid for 7 days.</p>' },
];

const CRM_GUIDE: QA[] = [
  { q: "How leads arrive in the CRM",
    a: '<p>Leads enter the CRM automatically from three sources:</p><ul><li><b>Consumer Tool</b> &mdash; customers who use your embedded kitchen planner on your website</li><li><b>Website form</b> &mdash; standard enquiry forms with The Kitchen OS tracking</li><li><b>Supplier referrals</b> &mdash; leads passed from supplier partner networks</li></ul><p>Each lead appears in the <b>Submissions</b> inbox first. Click <b>Convert to CRM</b> to move it into the pipeline.</p>' },
  { q: "Managing the pipeline",
    a: '<p>The CRM uses a 5-stage pipeline: <b>Lead &rarr; Qualified &rarr; Quoted &rarr; Won &rarr; Lost</b>. Drag cards between columns or use the status dropdown inside each record to move leads through.</p><p>Pipeline totals and conversion rates appear on your <b>Dashboard</b> and the <b>Reports</b> page in real time.</p>' },
  { q: "Customer records and history",
    a: '<p>Each CRM record holds the full customer history: all messages, quotes, bookings, deliveries, invoices and notes in one timeline. Click any customer name from anywhere in the portal to open their record.</p><div class="tip">&#128161; Use the <b>Notes</b> tab to log site survey findings, material preferences and any special requirements before the design consultation.</div>' },
  { q: "Contacts vs CRM",
    a: '<p><b>Contacts</b> is your address book &mdash; everyone you have ever dealt with. <b>CRM</b> shows only active pipeline records with a job in progress. A contact becomes a CRM record when you attach a project to them.</p>' },
];

const QUOTES_GUIDE: QA[] = [
  { q: "Creating and sending a quote",
    a: "<p>Open <b>Quote Builder</b>, select a customer, add rooms and drag in units. The margin calculator at the bottom shows your gross profit in real time. When ready, click <b>Send</b> &mdash; the customer receives a branded PDF and a live online viewer where they can approve and pay a deposit.</p>" },
  { q: "Revising a quote",
    a: "<p>Open the quote from the customer&rsquo;s record or from the <b>Reports</b> page. Design changes are tracked as a new quote version so previous versions are preserved and the customer can review every iteration from their customer portal. Automatic customer notification on revision is coming soon.</p>" },
  { q: "Deposits and payments",
    a: "<p>Connect <b>Stripe</b> or <b>GoCardless</b> in <b>Settings &rarr; Integrations</b>. Once connected, customers can pay deposits directly from the quote viewer. The deposit is automatically reconciled against the job in <b>Operations</b> and reflected in <b>Billing &amp; Payments</b>.</p>" },
  { q: "Quote PDF customisation",
    a: "<p>In <b>Settings &rarr; Branding</b> you can change the cover page colour, header logo, footer contact details, and add a cover letter and T&amp;Cs page that appends to every quote automatically.</p>" },
];

const LEADS_GUIDE: QA[] = [
  { q: "How the consumer lead gen tool works",
    a: "<p>The Kitchen OS provides an embeddable kitchen planner widget for your website. When a homeowner completes a design, their details and kitchen specification are submitted directly to your <b>Submissions</b> inbox &mdash; pre-qualified with budget, room size and style preferences already captured.</p><p>To customise the welcome heading, button label, font and privacy disclaimer before sharing it on your website, go to <b>Settings &rarr; Wizard Content</b>. Your brand colour and logo come from <b>Settings &rarr; Branding</b> and apply automatically.</p>" },
  { q: "Triaging submissions",
    a: "<p>In <b>Submissions</b>, each lead shows the source, budget, contact details and any notes. You can:</p><ul><li><b>Convert</b> &mdash; move to CRM and start a project</li><li><b>Archive</b> &mdash; remove from inbox without deleting</li><li><b>Reply</b> &mdash; send a direct message to the lead</li></ul>" },
  { q: "Lead scoring and prioritisation",
    a: '<p>Leads are automatically tagged by budget band (<b>high-budget</b>, <b>mid-range</b>, <b>entry</b>) and source. Use the filter bar in Submissions to sort by budget, date or source to prioritise your follow-ups.</p><div class="tip">&#128161; Aim to respond to high-budget leads within 2 hours &mdash; conversion rates drop significantly after 4 hours.</div>' },
];

const DELIVERY_GUIDE: QA[] = [
  { q: "Setting up a delivery",
    a: "<p>From <b>Delivery</b>, click <b>+ New delivery</b> and link it to a job. Enter the delivery date, address and any access notes. The delivery record is visible to your assigned driver in the <b>Driver Portal</b> at <code>thekitchenos.io/driver</code>.</p>" },
  { q: "Adding drivers",
    a: "<p>Go to <b>Delivery &rarr; Manage Drivers</b>. Click <b>Add driver</b>, enter their name and mobile number. They receive a login link by SMS. Drivers see only their assigned deliveries &mdash; no access to financials or CRM.</p>" },
  { q: "Courier tracking integration",
    a: "<p>Log a tracking number from DPD, DHL, Royal Mail, Parcelforce, APC or Evri on any delivery record. The number is stored against the delivery so you and your customer can check courier status. Live auto-detection of the courier and status pulls are coming soon &mdash; for now you can click through to the courier&rsquo;s site with the reference.</p>" },
  { q: "Customer delivery tracking",
    a: "<p>When a delivery is dispatched, the customer automatically receives an SMS and email with a tracking link. They can see live courier status without needing to log in. You can customise the message template in <b>Settings &rarr; Notifications</b>.</p>" },
];

const MESSAGING_GUIDE: QA[] = [
  { q: "How messaging works",
    a: "<p>The <b>Messages</b> page shows threaded conversations with customers and suppliers. Each thread is grouped by subject line so you can track multiple topics with the same person. Channels include in-app, email and SMS &mdash; all in one inbox.</p>" },
  { q: "Composing a new message",
    a: "<p>Click <b>+ Compose</b> on the Messages page. Select a recipient (customer or supplier), enter a subject and your message. All conversations stay on the platform &mdash; your customer reads them in their portal and replies there. The Kitchen OS sends them an email or SMS <em>notification</em> when you message so they don&rsquo;t miss it, but the message content itself only ever lives in the platform.</p>" },
  { q: "Supplier messages",
    a: '<p>Switch to the <b>Suppliers</b> tab on the Messages page to see all supplier threads separately. Order confirmations, delivery updates and price queries are all tracked here.</p><div class="tip">&#128161; Coming soon: supplier delivery update emails will land directly in your Messages inbox automatically, so you never miss a dispatch notification.</div>' },
  { q: "Message notifications",
    a: "<p>Enable browser push notifications (the bell icon in the top bar) to get instant alerts when a customer replies. You can also configure email forwarding in <b>Settings &rarr; Notifications</b>.</p>" },
];

const CALENDAR_GUIDE: QA[] = [
  { q: "Adding events and appointments",
    a: "<p>Click any slot in the <b>Calendar</b> or press <b>+ Add Event</b>. Event types include: Bookings / Consultations, Installations, Site Surveys, Follow-ups, Snags, Payments, Personal and Team. Each type has a colour code so you can scan the calendar at a glance.</p>" },
  { q: "Booking confirmations and reminders",
    a: "<p>When you create a booking linked to a customer, they receive an automatic confirmation email with an <em>add-to-calendar</em> link (Google, Apple, Outlook). A reminder is sent 24 hours before. You can adjust reminder timing in <b>Settings &rarr; Notifications</b>.</p>" },
  { q: "Month, week and day views",
    a: "<p>Use the <b>Month / Week / Day</b> buttons in the calendar toolbar to switch views. The week view shows hourly slots for precise scheduling. The day view is ideal for busy install days with multiple time-sensitive events.</p>" },
  { q: "Syncing with Google Calendar or Outlook",
    a: "<p>Go to <b>Settings &rarr; Integrations &rarr; Calendar Sync</b> to connect Google Calendar or Microsoft 365. Events created in The Kitchen OS appear in your personal calendar and vice versa.</p>" },
];

const DESIGNS_GUIDE: QA[] = [
  { q: "Uploading design files",
    a: "<p>Go to <b>Designs</b> in the main navigation and click <b>Upload</b>. Supported formats: PDF, PNG, JPG, DWG, SVG and WebP. Files are linked to a customer record so you can find them quickly from the CRM or the customer&rsquo;s record.</p>" },
  { q: "Organising and searching designs",
    a: "<p>Use the <b>search bar</b> at the top of the Designs page to filter by customer name, design type or notes. You can also filter by date uploaded and file type using the dropdowns.</p>" },
  { q: "Sharing designs with customers",
    a: "<p>Click the share icon on any design to generate a secure link. The customer can view (but not download) the file from their portal without needing a login. Links expire after 30 days by default &mdash; adjustable in <b>Settings</b>.</p>" },
  { q: "Version control",
    a: "<p>Upload revised designs to the same record &mdash; The Kitchen OS keeps all versions with timestamps. The customer always sees the latest version; older versions are accessible to you in the version history panel.</p>" },
];

const BILLING_GUIDE: QA[] = [
  { q: "Overview of Billing & Payments",
    a: "<p>The <b>Billing &amp; Payments</b> page is where your financial transactions will appear once Stripe Connect is fully wired &mdash; deposits received, balance payments and your The Kitchen OS subscription. The page is currently in preview mode while we finish the live payments integration.</p>" },
  { q: "Accepting deposits and payments",
    a: "<p>Stripe Connect is being wired into the platform now so customers will be able to pay deposits directly from the quote viewer, with payments reconciled automatically against the job. The Settings &rarr; Payments tab lets you choose your payout schedule preference today; full deposit collection from quotes is coming next.</p>" },
  { q: "Invoicing",
    a: "<p>Generate a VAT invoice from any job by clicking <b>Create Invoice</b> in the Operations page. Invoices are sent by email as a PDF and stored in the customer record. You can also download a CSV of all invoices for your accountant.</p>" },
  { q: "Your The Kitchen OS subscription",
    a: "<p>Your subscription details, next billing date and payment method are shown in <b>Billing &amp; Payments &rarr; Subscription</b>. To upgrade, downgrade or cancel, contact support or use the self-service portal linked from that page.</p>" },
];

const SUPPLIERS_GUIDE: QA[] = [
  { q: "Browsing the supplier catalogue",
    a: "<p>Go to <b>Suppliers</b> to browse connected trade accounts. Each supplier card shows your account status, current promotions and a link to their live catalogue. Click <b>Browse catalogue</b> to search products and add them directly to a quote.</p>" },
  { q: "Connecting a new supplier",
    a: "<p>In <b>Settings &rarr; Integrations</b>, find the supplier and click <b>Connect</b>. You will need your trade account number and, for some suppliers, an API key from their trade portal. Once connected, prices and stock sync automatically.</p>" },
  { q: "Supplier promotions and rebates",
    a: "<p>Active promotions from your connected suppliers appear in the <b>Suppliers</b> widget on your Dashboard and in the full Suppliers page. Rebate thresholds are tracked automatically &mdash; you will see a progress bar showing how close you are to the next rebate tier.</p>" },
];

const FAQ: QA[] = [
  { q: "How do I invite a team member?",
    a: "Go to <b>Settings &rarr; Team &amp; Permissions</b>, click <b>Invite user</b>, enter their email and pick a role (Owner, Manager, Designer or Fitter). They will receive an email with a secure sign-up link valid for 7 days." },
  { q: "Can I customise the quote PDF?",
    a: "Yes &mdash; open <b>Settings &rarr; Branding</b> to change cover colours, header logo and footer contact details. You can also add a cover letter and T&amp;Cs page that will be appended automatically to every quote." },
  { q: "How do deliveries and couriers work?",
    a: "Every order includes a <b>delivery record</b>. On the Deliveries page you can log tracking numbers from DPD, DHL, Royal Mail, Parcelforce, APC or Evri &mdash; The Kitchen OS auto-detects the courier and pulls status updates into the dashboard." },
  { q: "Where is my data stored?",
    a: "Your data is encrypted at rest on UK-based servers (AWS London) and backed up every 6 hours. You own your data &mdash; you can export a full ZIP of contacts, quotes and documents from <b>Settings &rarr; Data</b> at any time." },
  { q: "Is The Kitchen OS GDPR compliant?",
    a: "Yes. We are a UK data processor registered with the ICO. We provide DPAs, consent management, audit logs and customer-initiated data-access &amp; erasure tools built in." },
  { q: "How do payments and deposits work?",
    a: "Stripe Connect is being wired into the platform now. Once live, customers will pay deposits directly from the quote viewer and the balance will reconcile against the job automatically. The Settings &rarr; Payments tab already lets you choose your payout schedule preference." },
  { q: "Can I use The Kitchen OS on my phone or tablet?",
    a: "Yes &mdash; the portal is fully responsive and works on any phone or tablet browser. Home-screen installation (PWA) and push notifications are coming soon so you&rsquo;ll be able to launch the platform like a native app." },
  { q: "How do I get help if I am stuck?",
    a: "Use the <b>Contact Support</b> card below, or press <code>&#8984;K</code> and search for &ldquo;help&rdquo;. Our UK support team replies within 2 business hours during 9&ndash;5 Mon&ndash;Fri." },
  { q: "Can I import my existing customer list?",
    a: "Right now you can <b>export</b> your customer list as CSV from the Contacts page for backup, but bulk import isn&rsquo;t available yet. Add customers one at a time, or contact support if you have a large list you&rsquo;d like to bring across." },
  { q: "How does the anti-spam / anti-fraud system work?",
    a: "The Kitchen OS monitors for off-platform contact attempts (sharing personal phone numbers or emails in messages to bypass the platform). Flagged messages are reviewed by our trust &amp; safety team. This protects your commission and ensures all customer interactions are covered by our guarantee." },
];

interface Video { title: string; color: string }
const VIDEOS: Video[] = [
  { title: "Portal tour (4 min)",       color: "#e58200" },
  { title: "Building your first quote", color: "#3b82f6" },
  { title: "Connecting TKC & PWS",      color: "#22c55e" },
  { title: "Deliveries & courier sync", color: "#a855f7" },
];

interface Tip { icon: string; title: string; body: string }
const TIPS: Tip[] = [
  { icon: "&#9889;",   title: "Respond fast",       body: "Reply to high-budget leads within 2 hours. Conversion rates drop sharply after 4 hours." },
  { icon: "&#128247;", title: "Upload site photos", body: "Attach site survey photos to the CRM record before the design consultation." },
  { icon: "&#128200;", title: "Track your pipeline", body: 'Review your Sales dashboard weekly. Stale leads in "Design" stage cost you money.' },
  { icon: "&#128179;", title: "Take deposits online", body: "Customers who pay a deposit online are 3x more likely to proceed to full order." },
  { icon: "&#128241;", title: "Install the PWA",    body: "Add The Kitchen OS to your home screen for instant access and push notifications." },
  { icon: "&#9733;",   title: "Ask for reviews",    body: "After every completed install, use the Reviews widget to request a Google review automatically." },
];

// ── accordionHtml — verbatim port of source line 22551 ──────────────────────

function accordionHtml(items: QA[]): string {
  return items.map((it) => {
    const qLow = it.q.toLowerCase();
    const aLow = it.a.toLowerCase().replace(/<[^>]+>/g, "");
    return (
      '<div class="kh-acc" data-q="' + esc(qLow + " " + aLow) + '">' +
        '<div class="kh-acc-head"><span>' + esc(it.q) + '</span>' +
          '<svg class="ch" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</div>' +
        '<div class="kh-acc-body"><div class="kh-acc-body-inner">' + it.a + '</div></div>' +
      '</div>'
    );
  }).join("");
}

// ── buildContent — verbatim port of source line 22711 ───────────────────────

function buildContent(): string {
  const tipsHtml = TIPS.map((t) =>
    '<div class="kh-tip-card"><div class="icon">' + t.icon + '</div>' +
      '<h3>' + esc(t.title) + '</h3><p>' + t.body + '</p></div>',
  ).join("");

  const videosHtml = VIDEOS.map((v) =>
    '<div class="kh-vid" data-video-title="' + esc(v.title) + '" ' +
         'style="background:linear-gradient(135deg,' + v.color + '33,#0f0f12)">' +
      '<div class="play"><svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>' +
      '<div class="cap">' + esc(v.title) + '</div>' +
    '</div>',
  ).join("");

  return (
    '<div class="kh-hero">' +
      '<h1>Help &amp; Knowledge Base</h1>' +
      '<p>Guides, FAQs and walkthroughs for getting the most out of The Kitchen OS. ' +
         'Can&rsquo;t find what you need? Our UK team replies within 2 business hours.</p>' +
      '<div class="kh-search">' +
        '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
        '<input id="kh-search-input" type="search" ' +
               'placeholder="Search the knowledge base… (e.g. “invite team”, “deliveries”, “GDPR”)">' +
      '</div>' +
      '<div class="kh-hits" id="kh-hits"></div>' +
    '</div>' +

    '<div class="kh-section-label">Getting started</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>Getting Started</h2>' +
        '<div class="sub">First login, profile setup, connecting your showroom.</div>' +
        accordionHtml(GETTING_STARTED) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>CRM &amp; Leads</h2>' +
        '<div class="sub">Managing leads, customer records and the sales pipeline.</div>' +
        accordionHtml(CRM_GUIDE) +
      '</div>' +
    '</div>' +

    '<div class="kh-section-label">Quotes, orders &amp; payments</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>Quotes &amp; Orders</h2>' +
        '<div class="sub">Quote Builder, revisions, deposits and invoicing.</div>' +
        accordionHtml(QUOTES_GUIDE) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>Billing &amp; Payments</h2>' +
        '<div class="sub">Deposits, invoices, Stripe/GoCardless and your subscription.</div>' +
        accordionHtml(BILLING_GUIDE) +
      '</div>' +
    '</div>' +

    '<div class="kh-section-label">Operations</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>Delivery &amp; Tracking</h2>' +
        '<div class="sub">Setting up deliveries, driver management and customer tracking.</div>' +
        accordionHtml(DELIVERY_GUIDE) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Calendar &amp; Bookings</h2>' +
        '<div class="sub">Scheduling consultations, installs and syncing with Google/Outlook.</div>' +
        accordionHtml(CALENDAR_GUIDE) +
      '</div>' +
    '</div>' +

    '<div class="kh-section-label">Communication &amp; files</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>Messaging</h2>' +
        '<div class="sub">Communicating with customers and suppliers through the platform.</div>' +
        accordionHtml(MESSAGING_GUIDE) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>Design Uploads</h2>' +
        '<div class="sub">Uploading, organising and sharing design files with customers.</div>' +
        accordionHtml(DESIGNS_GUIDE) +
      '</div>' +
    '</div>' +

    '<div class="kh-section-label">Suppliers &amp; lead gen</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>Supplier Catalogue</h2>' +
        '<div class="sub">Browsing products, connecting accounts and tracking promotions.</div>' +
        accordionHtml(SUPPLIERS_GUIDE) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>Lead Gen Tool</h2>' +
        '<div class="sub">How leads come in, how they appear in CRM, and how to convert them.</div>' +
        accordionHtml(LEADS_GUIDE) +
      '</div>' +
    '</div>' +

    '<div class="kh-section-label">Tips &amp; best practices</div>' +
    '<div class="kh-tips-grid">' + tipsHtml + '</div>' +

    '<div class="kh-section-label" style="margin-top:28px">FAQs &amp; support</div>' +
    '<div class="kh-grid">' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>Frequently Asked Questions</h2>' +
        '<div class="sub">Quick answers to the most common questions.</div>' +
        accordionHtml(FAQ) +
      '</div>' +
      '<div class="kh-card">' +
        '<h2><svg viewBox="0 0 24 24"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>Video Tutorials</h2>' +
        '<div class="sub">Short walkthroughs of key workflows. Coming soon.</div>' +
        '<div class="kh-vids">' + videosHtml + '</div>' +
        '<div class="kh-card kh-support" style="margin-top:16px">' +
          '<h2><svg viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>Contact Support</h2>' +
          '<div class="sub" style="padding-left:0">Real humans, UK-based, 9&ndash;5 Mon&ndash;Fri (2-hour reply SLA).</div>' +
          '<div class="row">' +
            '<a href="mailto:info@kitchengine.io"><svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>info@kitchengine.io</a>' +
            '<a href="tel:+441134960000"><svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>0113 496 0000</a>' +
            '<a href="#" data-help-replay-onboarding="1"><svg viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>Replay onboarding</a>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const helpPage: Page = {
  name: "help",
  label: "Help & Guides",
  icon: "",
  hidden: true,

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    container.innerHTML = '<div class="page-section" id="page-help">' + buildContent() + '</div>';
    const root = container.querySelector<HTMLElement>("#page-help")!;

    // Accordion toggle — delegated, scoped to this page root.
    const onClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const replay = target.closest<HTMLElement>("[data-help-replay-onboarding]");
      if (replay) {
        e.preventDefault();
        ctx.showToast("Onboarding replay coming soon", "info");
        return;
      }

      const vid = target.closest<HTMLElement>(".kh-vid");
      if (vid) {
        const title = vid.getAttribute("data-video-title") ?? "Video";
        ctx.showToast(`${title} — coming soon, our team is recording!`, "info");
        return;
      }

      const head = target.closest<HTMLElement>(".kh-acc-head");
      if (head) {
        const acc = head.parentElement;
        if (acc && acc.classList.contains("kh-acc")) acc.classList.toggle("open");
      }
    };
    root.addEventListener("click", onClick);

    // Search filter — open matches, hide non-matches, count hits.
    const input = root.querySelector<HTMLInputElement>("#kh-search-input");
    const hits = root.querySelector<HTMLElement>("#kh-hits");
    const onInput = (): void => {
      const q = (input?.value || "").toLowerCase().trim();
      const accs = root.querySelectorAll<HTMLElement>(".kh-acc");
      let count = 0;
      accs.forEach((a) => {
        const hay = a.getAttribute("data-q") ?? "";
        if (!q || hay.indexOf(q) >= 0) {
          a.classList.remove("kh-hide");
          if (q) a.classList.add("open");
          count++;
        } else {
          a.classList.add("kh-hide");
          a.classList.remove("open");
        }
      });
      if (hits) hits.textContent = q ? `${count} result${count === 1 ? "" : "s"} for "${q}"` : "";
    };
    input?.addEventListener("input", onInput);

    return (): void => {
      root.removeEventListener("click", onClick);
      input?.removeEventListener("input", onInput);
    };
  },
};

registerPage(helpPage);
